import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { takeUntil, Subject, switchMap, filter, take, lastValueFrom } from 'rxjs';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { MajorCustomerAnalyData } from '../../models/common.model';

interface ItemOption extends Option {
  description?: string;
}

@Component({
  selector: 'app-major-customer-analysis',
  templateUrl: './major-customer-analysis.component.html',
  styleUrls: ['./major-customer-analysis.component.scss']
})
export class MajorCustomerAnalysisComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'customerId';
  override rowDataDeleteHintFieldName: string = 'customerNumber';
  override componentClassName: string = 'MajorCustomerAnalysisComponent';

  override showActions: boolean = false;
  override showNoDataFoundMessage: boolean = false;

  // OU AutoComplete
  ouSuggestions: Option[] = [];
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Item Number AutoComplete
  itemSuggestions: ItemOption[] = [];
  private readonly itemSubject$ = new Subject<string | undefined>();

  queryId: string = '';
  totalQty: number = 0;
  totalAmt: number = 0;

  private resolvedInventoryItemId: number = 0;
  private resolvedDefaultOu: Option | null = null;

  constructor(
    private readonly stockSummaryService: StockSummaryService,
    private readonly commonService: CommonService,
    private readonly authService: AuthService
  ) {
    super();
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    const ouValue = this.formControl('ouId').value;
    const itemValue = this.formControl('itemNumber').value;
    const startDate: string = this.formControl('startDate').value ?? '';
    const endDate: string = this.formControl('endDate').value ?? '';

    const ouId = ouValue?.id ? Number(ouValue.id) : 0;
    const parsedItemId = Number(itemValue?.id);
    const inventoryItemId = (itemValue?.id != null && !isNaN(parsedItemId)) ? parsedItemId : 0;
    const empNo: string = this.authService.getUser()?.username ?? '';
    const uuid = crypto.randomUUID();

    try {
      const res = await lastValueFrom(
        this.commonService.getMajorCustomerAnalyData({
          ouId,
          empNo,
          inventoryItemId,
          startDate,
          endDate,
          uuid,
        })
      );
      this.queryId = res.queryId ?? '';
      this.detailDatas = res.responseData ?? [];
      this.totalQty = res.total?.qty ?? 0;
      this.totalAmt = res.total?.amt ?? 0;
    } catch {
      this.detailDatas = [];
      this.totalQty = 0;
      this.totalAmt = 0;
    }
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }
  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }
  override initExtraDetailMenu(): void {
    return;
  }
  override setExtraDetailMenuStatus(): void {
    return;
  }

  override initTableColumns(): void {
    this.tableColumns = [
      { header: 'field.cust-number', field: 'customerNumber', sortable: true },
      { header: 'field.cust-name', field: 'customerName', sortable: true },
      { header: 'field.qty', field: 'qty', sortable: true, numberField: true },
      { header: 'field.amt', field: 'amt', sortable: true, numberField: true },
      { header: 'field.share', field: 'shares', sortable: true },
      { header: 'field.accu', field: 'accu', sortable: true },
    ];
  }

  override initDialogConfig(dataAction: DataAction): void {
    return;
  }
  override initExtraRowActionMenu(): void {
    return;
  }
  override setExtraRowActionMenuStatus(): void {
    return;
  }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }
  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override initFormGroup(): void {
    const now = new Date();
    const defaultYM = now.getFullYear().toString() + (now.getMonth() + 1).toString().padStart(2, '0');

    this.formGroup = this.formBuilder.nonNullable.group({
      ouId: ['', Validators.required],
      startDate: [defaultYM, Validators.required],
      endDate: [defaultYM, Validators.required],
      itemNumber: ['', Validators.required],
      itemDesc: [{ value: '', disabled: true }],
    });
  }

  override initStaticOptions(): void {
    this.formControl('startDate').valueChanges
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.validateDateRange());
    this.formControl('endDate').valueChanges
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.validateDateRange());
  }

  private readonly ymPattern = /^\d{4}(0[1-9]|1[0-2])$/;

  private validateDateRange(): void {
    const startCtrl = this.formControl('startDate');
    const endCtrl = this.formControl('endDate');
    const startVal: string = startCtrl.value ?? '';
    const endVal: string = endCtrl.value ?? '';

    // startDate errors
    const startErrors = { ...startCtrl.errors };
    delete startErrors['format'];
    delete startErrors['dateRange'];
    if (startVal && !this.ymPattern.test(startVal)) {
      startErrors['format'] = true;
    } else if (startVal && endVal && this.ymPattern.test(startVal) && this.ymPattern.test(endVal) && startVal > endVal) {
      startErrors['dateRange'] = true;
    }
    startCtrl.setErrors(Object.keys(startErrors).length ? startErrors : null);

    // endDate errors
    const endErrors = { ...endCtrl.errors };
    delete endErrors['format'];
    if (endVal && !this.ymPattern.test(endVal)) {
      endErrors['format'] = true;
    }
    endCtrl.setErrors(Object.keys(endErrors).length ? endErrors : null);
  }

  override async initFormData(): Promise<any> {
    const data = this.dynamicDialogConfig.data;
    if (!data?.ou) {
      const orgs = await lastValueFrom(this.stockSummaryService.getOrgs().pipe(take(1))).catch(() => []);
      this.resolvedDefaultOu = orgs[0] ?? null;
    }
    if (data?.itemNumber) {
      const items = await lastValueFrom(
        this.commonService.getItemsWithDesc(data.itemNumber, data.inventoryItemId).pipe(take(1))
      ).catch(() => []);
      const match = items.find((i: any) => i.value === data.itemNumber || i.label === data.itemNumber);
      if (match) {
        this.resolvedInventoryItemId = match.id ? Number(match.id) : 0;
        if (match.description) {
          this.formGroup.patchValue({ itemDesc: match.description });
        }
      }
    }
    return true;
  }

  override afterLangChange(): void {
    return;
  }

  override afterFormDataLoadSuccess(): void {
    const data = this.dynamicDialogConfig.data;

    const patches: any = {};
    if (data?.ou) {
      patches.ouId = data.ou;
    } else if (this.resolvedDefaultOu) {
      patches.ouId = this.resolvedDefaultOu;
    }
    if (data?.itemNumber) {
      patches.itemNumber = {
        id: this.resolvedInventoryItemId || data.inventoryItemId || data.itemNumber,
        value: data.itemNumber,
        label: data.itemNumber,
      };
    }

    if (Object.keys(patches).length === 0) return;

    this.formGroup.patchValue(patches);
    this.changeDetectorRef.detectChanges();

    if (data?.ou && data?.itemNumber) {
      setTimeout(() => this.query());
    }
  }

  override afterFormDataLoadFail(): void {
    return;
  }
  override initFormControlDisabled(): void {
    return;
  }

  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getItemsWithDesc(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => {
            this.itemSuggestions = data;
          },
          error: () => {
            this.itemSuggestions = [];
          },
        });
    }
    this.itemSubject$.next(event.query);
  }

  onItemSelect(event: { value: ItemOption }): void {
    const selected = event.value;
    if (selected?.description) {
      this.formGroup.patchValue({ itemDesc: selected.description });
    } else {
      this.formGroup.patchValue({ itemDesc: '' });
    }
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onItemClear(): void {
    this.formGroup.patchValue({ itemDesc: '' });
    this.autoCompleteCleared('autoCompleteCode');
  }

  override searchReset(): void {
    this.totalQty = 0;
    this.totalAmt = 0;
    this.queryId = '';
    super.searchReset();
  }
}
